import { useMemo } from 'react';
import type { Cotacao } from '@/db';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(value);
}

interface BarItem {
  label: string;
  value: number;
  display: string;
  colorClass: string;
}

function HorizontalBarChart({ title, data, max }: { title: string; data: BarItem[]; max: number }) {
  return (
    <div className="bg-white rounded-xl border border-background-200/70 p-5">
      <h3 className="font-heading text-base font-semibold text-foreground-950 mb-4">{title}</h3>
      <div className="space-y-3">
        {data.map((item, i) => {
          const pct = max > 0 ? (item.value / max) * 100 : 0;
          return (
            <div key={i} className="flex items-center gap-3">
              <span className="text-xs text-foreground-900 font-medium w-32 truncate text-right shrink-0" title={item.label}>
                {item.label}
              </span>
              <div className="flex-1 h-7 bg-background-100 rounded-md overflow-hidden relative">
                <div
                  className={`h-full rounded-md ${item.colorClass} transition-all duration-700 ease-out`}
                  style={{ width: `${pct}%` }}
                />
                <span className="absolute left-2 top-1/2 -translate-y-1/2 px-1.5 py-0.5 rounded bg-white/90 text-[11px] font-bold text-slate-950 font-tabular shadow-sm">
                  {item.display}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LineChart({ title, data, max }: { title: string; data: BarItem[]; max: number }) {
  const width = 620;
  const height = 220;
  const left = 18;
  const right = 18;
  const top = 30;
  const bottom = 38;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const points = data.map((item, index) => ({
    ...item,
    x: data.length === 1 ? width / 2 : left + (index / Math.max(data.length - 1, 1)) * plotWidth,
    y: top + plotHeight - (item.value / Math.max(max, 1)) * plotHeight,
  }));
  const line = points.map((point) => `${point.x},${point.y}`).join(' ');
  const area = points.length ? `${left},${top + plotHeight} ${line} ${points.at(-1)!.x},${top + plotHeight}` : '';
  return (
    <div className="bg-white rounded-xl border border-background-200/70 p-5">
      <h3 className="font-heading text-base font-semibold text-foreground-950 mb-4">{title}</h3>
      {points.length === 0 ? <div className="h-52 flex items-center justify-center text-sm text-foreground-700">Sem dados no período selecionado.</div> :
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-52" role="img" aria-label={`${title}: ${data.map((item) => `${item.label}, ${item.display}`).join('; ')}`}>
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => <line key={ratio} x1={left} x2={width - right} y1={top + plotHeight * ratio} y2={top + plotHeight * ratio} stroke="#dbe5f1" strokeWidth="1" />)}
        {points.length > 1 && <polygon points={area} fill="#d9e9fb" opacity="0.7" />}
        {points.length > 1 && <polyline points={line} fill="none" stroke="#0b3978" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />}
        {points.map((point) => <g key={point.label}>
          <circle cx={point.x} cy={point.y} r="6" fill="#ffffff" stroke="#0b3978" strokeWidth="4" />
          <text x={point.x} y={Math.max(point.y - 13, 14)} textAnchor="middle" fontSize="11" fontWeight="700" fill="#111827">{point.display}</text>
          <text x={point.x} y={height - 12} textAnchor="middle" fontSize="11" fontWeight="600" fill="#1f2937">{point.label}</text>
        </g>)}
      </svg>}
    </div>
  );
}

interface ChartsPanelProps {
  cotacoes: Cotacao[];
}

export default function ChartsPanel({ cotacoes }: ChartsPanelProps) {
  const {
    topClientes,
    porMes,
    topProdutos,
    porEstado,
  } = useMemo(() => {
    // Top clientes por volume
    const clientesVol: Record<string, number> = {};
    const clientesName: Record<string, string> = {};
    cotacoes.forEach((c) => {
      clientesVol[c.clienteCodigo] = (clientesVol[c.clienteCodigo] || 0) + (c.totalBrl || c.total);
      clientesName[c.clienteCodigo] = c.clienteNome;
    });
    const topClientes = Object.entries(clientesVol)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([codigo, value], i) => ({
        label: clientesName[codigo] || codigo,
        value,
        display: formatCurrency(value),
        colorClass: ['bg-primary-500', 'bg-primary-400', 'bg-primary-300', 'bg-primary-200', 'bg-primary-100'][i] || 'bg-primary-100',
      }));

    // Por mês
    const meses: Record<string, number> = {};
    cotacoes.forEach((c) => {
      const d = new Date(c.createdAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      meses[key] = (meses[key] || 0) + (c.totalBrl || c.total);
    });
    const mesKeys = Object.keys(meses).sort();
    const porMes = mesKeys.map((key, i) => {
      const [year, month] = key.split('-');
      const label = `${month}/${year.slice(2)}`;
      return {
        label,
        value: meses[key],
        display: formatCurrency(meses[key]),
        colorClass: ['bg-accent-500', 'bg-accent-400', 'bg-accent-300', 'bg-accent-200', 'bg-accent-100', 'bg-accent-50'][i] || 'bg-accent-100',
      };
    });

    // Top produtos
    const produtosVol: Record<string, number> = {};
    cotacoes.forEach((c) => {
      const itemBase = c.itens.reduce((sum, item) => sum + Math.max(item.precoUnitarioConvertido || item.precoNegociado, 0), 0);
      c.itens.forEach((item) => {
        const weight = itemBase > 0 ? Math.max(item.precoUnitarioConvertido || item.precoNegociado, 0) / itemBase : 1 / c.itens.length;
        produtosVol[item.materialDescricao] = (produtosVol[item.materialDescricao] || 0) + (c.totalBrl || c.total) * weight;
      });
    });
    const topProdutos = Object.entries(produtosVol)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([label, value], i) => ({
        label,
        value,
        display: formatCurrency(value),
        colorClass: ['bg-secondary-500', 'bg-secondary-400', 'bg-secondary-300', 'bg-secondary-200', 'bg-secondary-100'][i] || 'bg-secondary-100',
      }));

    // Por estado
    const estadoVol: Record<string, number> = {};
    // We don't have estado directly in cotacao, but we can approximate from clienteNome
    // Actually, for this mock we won't have UF easily without joining. We'll use a simplified approach
    // Since we don't have clientes data readily available in this component without extra query,
    // let's just count by the first word of clienteNome as a region proxy, OR skip this.
    // Actually, better: let's just count by moeda
    const moedaVol: Record<string, number> = {};
    cotacoes.forEach((c) => {
      moedaVol[c.moeda] = (moedaVol[c.moeda] || 0) + (c.totalBrl || c.total);
    });
    const porEstado = Object.entries(moedaVol)
      .sort((a, b) => b[1] - a[1])
      .map(([label, value], i) => ({
        label: label === 'BRL' ? 'Real (BRL)' : label === 'USD' ? 'Dólar (USD)' : 'Euro (EUR)',
        value,
        display: formatCurrency(value),
        colorClass: ['bg-primary-500', 'bg-accent-500', 'bg-secondary-500'][i] || 'bg-secondary-300',
      }));

    return { topClientes, porMes, topProdutos, porEstado };
  }, [cotacoes]);

  return (
    <div className="space-y-4 md:space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
        <HorizontalBarChart
          title="Top 5 Clientes por Volume"
          data={topClientes}
          max={topClientes[0]?.value || 1}
        />
        <LineChart
          title="Volume por Mês"
          data={porMes}
          max={porMes.reduce((m, d) => Math.max(m, d.value), 0) || 1}
        />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
        <HorizontalBarChart
          title="Top 5 Produtos por Volume"
          data={topProdutos}
          max={topProdutos[0]?.value || 1}
        />
        <HorizontalBarChart
          title="Volume por Moeda"
          data={porEstado}
          max={porEstado[0]?.value || 1}
        />
      </div>
    </div>
  );
}
